// The live session with one target (src/voitta_kvm/app/hub.py): frames, state, the run stream, and input.
//
// The run stream (protocol 2): every run message carries seq 1, 2, ...; timeline.apply takes them strictly in order.
// After a gap (a reconnect, a page opened mid-run, a message filtered for an older page) this asks the server for
// everything after the last seq it has (`resume`); when that is gone the server says `reset` and the chat reloads the
// run's history. The timeline lives across reconnects; it starts over only when the target changes.
import { useEffect, useRef, useState } from "react";
import type { Ctx } from "./ContextMeter";
import { apply, empty, unfinished, withHistory, type HistoryRun, type Msg, type Timeline } from "./timeline";

export type RunState = "connecting" | "idle" | "preparing" | "running" | "stopping" | "human" | "busy" | "error" | "closed";
export type Marks = Record<string, unknown>;
export const PROTO = 2;

export type Live = {
  state: RunState;
  holder: string | null;
  via: string | null;              // how the holder holds it: web (a tab) or mcp (their own Claude Code)
  what: string | null;             // busy because its KVM shows another computer: "KVM1 is showing Lab PC"
  driver: string | null;           // who drives the run now: "mcp" while a person's Claude Code does (chat read-only)
  error: string | null;
  screen: [number, number] | null;
  pointer: "relative" | "absolute";
  marks: Marks;
  run: string | null;
  conversation: string | null;
  phase: string | null;            // which part of start-up is going (protocol 1 servers)
  timeline: Timeline;
  historyWanted: number;           // bumps when a reset means the open conversation's history must be loaded again
  proto: number;                   // what the server speaks
  ctx: Record<string, Ctx>;        // how full each conversation's context is (the meter)
  setConversationCtx: (c: Ctx) => void;   // from a conversation's record, unless newer arrived live
  loadHistory: (conversation: string, runs: HistoryRun[]) => void;
  send: (msg: Record<string, unknown>) => boolean;   // false: not connected, nothing sent
  onFrame: (fn: (bmp: ImageBitmap) => void) => void;
};

export function useLive(targetId: string | null): Live {
  const [state, setState] = useState<RunState>("connecting");
  const [holder, setHolder] = useState<string | null>(null);
  const [via, setVia] = useState<string | null>(null);
  const [what, setWhat] = useState<string | null>(null);
  const [driver, setDriver] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [screen, setScreen] = useState<[number, number] | null>(null);
  const [pointer, setPointer] = useState<"relative" | "absolute">("relative");
  const [marks, setMarks] = useState<Marks>({});
  const [run, setRun] = useState<string | null>(null);
  const [conversation, setConversation] = useState<string | null>(null);
  const [phase, setPhase] = useState<string | null>(null);
  const [proto, setProto] = useState(1);
  const [timeline, setTimeline] = useState<Timeline>(empty);
  const [ctx, setCtx] = useState<Record<string, Ctx>>({});         // the context meter, per conversation (latest)
  const [historyWanted, setHistoryWanted] = useState(0);
  const ws = useRef<WebSocket | null>(null);
  const frameFn = useRef<((b: ImageBitmap) => void) | null>(null);
  const tl = useRef<Timeline>(empty());
  const raf = useRef(0);
  const asked = useRef<Record<string, { seq: number; at: number }>>({});

  // many messages can arrive at once (a resume): apply them all, draw once per frame
  // (a hidden tab gets no animation frames: there it draws on a short timer, so it is current when shown again)
  const commit = () => {
    if (raf.current) return;
    const draw = () => { raf.current = 0; setTimeline(tl.current); };
    raf.current = document.hidden ? -window.setTimeout(draw, 250) : requestAnimationFrame(draw);
  };

  useEffect(() => {
    if (!targetId) return;
    setState("connecting"); setError(null); setScreen(null); setRun(null); setConversation(null); setWhat(null);
    tl.current = empty(); asked.current = {}; setTimeline(tl.current);
    // Reconnects wait 2 s, then twice as long each time it fails, up to a minute; a connection that opens resets it.
    // A computer that could not be opened (unreachable) was retried every 2-3 s for hours: 1,200 openings an hour on
    // the server, each with its own log file (Lab PC 2 offline, 2026-10-22).
    let stopped = false, retry: number | undefined, wait = 2000;

    const resume = (s: WebSocket, runId: string, seq: number) => {
      const a = asked.current[runId], now = Date.now();
      if (a && a.seq === seq && now - a.at < 3000) return;          // asked already: the answer is on its way
      asked.current[runId] = { seq, at: now };
      if (s.readyState === WebSocket.OPEN) s.send(JSON.stringify({ t: "resume", run: runId, seq }));
    };
    const take = (s: WebSocket, m: Msg) => {
      const res = apply(tl.current, m);
      tl.current = res.tl;
      if (res.need?.resume) resume(s, res.need.resume.run, res.need.resume.seq);
      if (res.need?.history) setHistoryWanted((x) => x + 1);
      commit();
    };

    const open = () => {
      const scheme = location.protocol === "https:" ? "wss" : "ws";
      const s = new WebSocket(`${scheme}://${location.host}/ws/targets/${targetId}`);
      s.binaryType = "blob";
      ws.current = s;
      s.onmessage = async (m) => {
        if (typeof m.data !== "string") {
          // the server sends the next frame only after this ack: one frame in flight, so the link never queues video
          try {
            const bmp = await createImageBitmap(m.data as Blob);
            if (frameFn.current) frameFn.current(bmp); else bmp.close();
          } catch { /* a broken frame */ }
          if (s.readyState === WebSocket.OPEN) s.send('{"t":"ack"}');
          return;
        }
        const d = JSON.parse(m.data);
        switch (d.t) {
          case "hello": {
            wait = 2000;
            setError(null);                              // a successful reopening clears the previous failure
            setHolder(d.holder); setState(d.state); setRun(d.run); setConversation(d.conversation);
            setDriver(d.driver ?? null);
            setProto(d.proto ?? 1);
            setPointer(d.pointer === "absolute" ? "absolute" : "relative");
            if (d.screen) setScreen(d.screen);
            if ((d.proto ?? 1) >= 2) s.send(JSON.stringify({ t: "hello", proto: PROTO }));
            // a run going: get what this page has not seen of it (all of it, for a page that just opened)
            if (d.run) { asked.current[d.run] = { seq: -1, at: 0 }; resume(s, d.run, tl.current.byId[d.run]?.seq ?? 0); }
            // a run this page still shows going may have ended while it was away: its rest, or a reset and its history
            for (const u of unfinished(tl.current)) {
              if (u.run !== d.run) { asked.current[u.run] = { seq: -1, at: 0 }; resume(s, u.run, u.seq); }
            }
            break;
          }
          case "state": setState(d.state); setRun(d.run); if (d.conversation) setConversation(d.conversation);
            setDriver(d.driver ?? null);
            if (d.state !== "preparing") setPhase(null); break;
          case "phase": setPhase(d.part); break;
          case "screen": setScreen(d.size); break;
          case "marks": setMarks(d.marks); break;
          case "busy": setHolder(d.holder); setVia(d.via ?? "web"); setWhat(d.what ?? null); setState("busy"); stopped = true; break;
          case "released": setState("closed"); stopped = true; s.close(); break;   // this tab gave the computer up
          case "error": setError(d.message); break;
          case "ctx": setCtx((c) => ({ ...c, [d.conversation]: d })); break;
          case "reset": take(s, d); break;
          default:
            if (typeof d.seq === "number") {
              if (d.t === "ev" && d.kind === "run.start") { setRun(d.run); setConversation(d.conversation); }
              take(s, d);
            }
        }
      };
      s.onclose = (e) => {
        ws.current = null;
        if (stopped) return;
        if (e.code === 4401) { location.href = "/auth/login"; return; }
        setState("closed");
        if (e.code === 4403) return;        // the computer refused the login: retrying would only fail it again
        retry = window.setTimeout(open, wait);              // the server restarted, the link dropped, or it cannot open
        wait = Math.min(60000, wait * 2);
      };
    };
    open();
    return () => {
      stopped = true; clearTimeout(retry);
      if (raf.current > 0) cancelAnimationFrame(raf.current); else clearTimeout(-raf.current);
      raf.current = 0; ws.current?.close();
    };
  }, [targetId]);

  return {
    state, holder, via, what, driver, error, screen, pointer, marks, run, conversation, phase, timeline, historyWanted, proto, ctx,
    setConversationCtx: (c: Ctx) => setCtx((all) => all[c.conversation] && (all[c.conversation].at ?? 0) >= (c.at ?? 0) ? all : { ...all, [c.conversation]: c }),
    loadHistory: (conv, runs) => { tl.current = withHistory(tl.current, conv, runs); commit(); },
    send: (msg) => {
      if (ws.current?.readyState !== WebSocket.OPEN) return false;
      ws.current.send(JSON.stringify(msg));
      return true;
    },
    onFrame: (fn) => { frameFn.current = fn; },
  };
}
